import Phaser from 'phaser';
import type { LevelTheme } from '../endless/levels';

/** Theme-independent item/effect textures (generated once). */
export const ITEM_TEXTURES = {
  bone: 'item-bone',
  golden: 'item-golden',
  feather: 'item-feather',
  shield: 'item-shield',
  magnet: 'item-magnet',
  boots: 'item-boots',
  spikes: 'item-spikes',
  spring: 'item-spring',
  flag: 'item-flag',
  heart: 'item-heart',
  dust: 'item-dust',
  bubble: 'item-bubble',
  roller: 'item-roller',
  beetle: 'item-beetle',
  bat: 'item-bat',
  rock: 'item-rock',
  wind: 'item-wind',
  ice: 'item-ice',
} as const;

export const TILE = {
  ground: 64,
  groundHeight: 256,
  cap: 24,
  soil: 64,
  stone: 32,
  plank: 32,
} as const;

export function themeKeys(theme: LevelTheme) {
  return {
    sky: `theme-${theme.id}-sky`,
    ground: `theme-${theme.id}-ground`,
    stone: `theme-${theme.id}-stone`,
    plank: `theme-${theme.id}-plank`,
    crumble: `theme-${theme.id}-crumble`,
    hills: `theme-${theme.id}-hills`,
    clouds: `theme-${theme.id}-clouds`,
    /** Grass cap strip + plain soil tile, so tall ground blocks show one grass line. */
    cap: `theme-${theme.id}-cap`,
    soil: `theme-${theme.id}-soil`,
  };
}

export const PARALLAX = { hillsW: 512, hillsH: 220, cloudsW: 512, cloudsH: 140 } as const;

function graphics(scene: Phaser.Scene): Phaser.GameObjects.Graphics {
  return scene.make.graphics({ x: 0, y: 0 }, false);
}

/**
 * Vertical gradient as horizontal bands. `fillGradientStyle` is WebGL-only and `generateTexture`
 * rasterises through a canvas, where it falls back to a flat fill — bands work everywhere.
 */
export function fillVerticalGradient(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  top: number,
  bottom: number,
  bands = 48,
): void {
  const a = Phaser.Display.Color.IntegerToColor(top);
  const b = Phaser.Display.Color.IntegerToColor(bottom);
  const bandH = h / bands;
  for (let i = 0; i < bands; i++) {
    const t = i / (bands - 1);
    const c = Phaser.Display.Color.Interpolate.ColorWithColor(a, b, 1, t);
    g.fillStyle(Phaser.Display.Color.GetColor(c.r, c.g, c.b), 1);
    g.fillRect(x, y + Math.floor(i * bandH), w, Math.ceil(bandH) + 1);
  }
}

/** Generates a theme's tiles once per game (they live in the texture manager). */
export function ensureThemeTextures(
  scene: Phaser.Scene,
  theme: LevelTheme,
  viewHeight: number,
): void {
  const keys = themeKeys(theme);
  if (scene.textures.exists(keys.sky)) return;
  const g = graphics(scene);

  fillVerticalGradient(g, 0, 0, 64, viewHeight, theme.sky[0], theme.sky[1]);
  g.generateTexture(keys.sky, 64, viewHeight);
  g.clear();

  const gt = TILE.ground;
  const gh = TILE.groundHeight;
  g.fillStyle(theme.soil, 1);
  g.fillRect(0, 0, gt, gh);
  g.fillStyle(theme.soilDark, 1);
  for (let i = 0; i < 18; i++) g.fillRect((i * 23) % gt, 18 + ((i * 29) % (gh - 30)), 9, 4);
  g.fillStyle(theme.grass, 1);
  g.fillRect(0, 0, gt, 10);
  g.fillStyle(Phaser.Display.Color.IntegerToColor(theme.grass).darken(18).color, 1);
  g.fillRect(0, 8, gt, 3);
  g.generateTexture(keys.ground, gt, gh);
  g.clear();

  // Grass cap (tiled along the top edge of a ground block) and plain soil (tiled below it).
  g.fillStyle(theme.soil, 1);
  g.fillRect(0, 0, gt, TILE.cap);
  g.fillStyle(theme.grass, 1);
  g.fillRect(0, 0, gt, 10);
  g.fillStyle(Phaser.Display.Color.IntegerToColor(theme.grass).darken(18).color, 1);
  g.fillRect(0, 8, gt, 3);
  g.generateTexture(keys.cap, gt, TILE.cap);
  g.clear();
  g.fillStyle(theme.soil, 1);
  g.fillRect(0, 0, TILE.soil, TILE.soil);
  g.fillStyle(theme.soilDark, 1);
  for (let i = 0; i < 6; i++) g.fillRect((i * 23) % TILE.soil, (i * 29) % (TILE.soil - 6), 9, 4);
  g.generateTexture(keys.soil, TILE.soil, TILE.soil);
  g.clear();

  const st = TILE.stone;
  g.fillStyle(theme.stone, 1);
  g.fillRect(0, 0, st, st);
  g.lineStyle(2, theme.stoneDark, 1);
  g.strokeRect(1, 1, st - 2, st - 2);
  g.fillStyle(Phaser.Display.Color.IntegerToColor(theme.stone).lighten(12).color, 1);
  g.fillRect(3, 3, st - 6, 5);
  g.generateTexture(keys.stone, st, st);
  g.clear();

  const pw = TILE.plank;
  g.fillStyle(theme.plank, 1);
  g.fillRect(0, 0, pw, 20);
  g.fillStyle(theme.plankLight, 1);
  g.fillRect(0, 0, pw, 4);
  g.fillStyle(Phaser.Display.Color.IntegerToColor(theme.plank).darken(25).color, 1);
  g.fillRect(0, 16, pw, 4);
  g.fillRect(pw / 2 - 1, 4, 2, 12);
  g.generateTexture(keys.plank, pw, 20);
  g.clear();

  // Crumbling plank: paler with cracks.
  g.fillStyle(Phaser.Display.Color.IntegerToColor(theme.plank).lighten(20).color, 1);
  g.fillRect(0, 0, pw, 20);
  g.fillStyle(theme.plankLight, 1);
  g.fillRect(0, 0, pw, 3);
  g.lineStyle(1, 0x3a2a1a, 0.9);
  g.lineBetween(6, 3, 12, 12);
  g.lineBetween(12, 12, 9, 19);
  g.lineBetween(22, 2, 19, 10);
  g.lineBetween(19, 10, 26, 18);
  g.generateTexture(keys.crumble, pw, 20);
  g.clear();

  // Parallax hills: two rows of soft mounds in a sky-tinted shade of the grass.
  const skyC = Phaser.Display.Color.IntegerToColor(theme.sky[1]);
  const grassC = Phaser.Display.Color.IntegerToColor(theme.grass);
  const far = Phaser.Display.Color.Interpolate.ColorWithColor(skyC, grassC, 1, 0.35);
  const near = Phaser.Display.Color.Interpolate.ColorWithColor(skyC, grassC, 1, 0.6);
  const { hillsW, hillsH, cloudsW, cloudsH } = PARALLAX;
  g.fillStyle(Phaser.Display.Color.GetColor(far.r, far.g, far.b), 1);
  for (const [cx, r] of [
    [60, 150],
    [250, 190],
    [430, 140],
  ] as const)
    g.fillCircle(cx, hillsH + 40, r);
  g.fillStyle(Phaser.Display.Color.GetColor(near.r, near.g, near.b), 1);
  for (const [cx, r] of [
    [150, 120],
    [360, 150],
    [512, 100],
    [0, 100],
  ] as const)
    g.fillCircle(cx, hillsH + 60, r);
  g.generateTexture(keys.hills, hillsW, hillsH);
  g.clear();

  // Clouds: three soft clusters, translucent white.
  g.fillStyle(0xffffff, 0.55);
  for (const [cx, cy, w] of [
    [90, 60, 140],
    [280, 40, 110],
    [430, 80, 160],
  ] as const) {
    g.fillEllipse(cx, cy, w, 34);
    g.fillEllipse(cx - w / 4, cy + 10, w / 2, 30);
    g.fillEllipse(cx + w / 4, cy + 8, w / 2, 28);
  }
  g.generateTexture(keys.clouds, cloudsW, cloudsH);
  g.destroy();
}

/** Generates the item, hazard and HUD textures once per game. */
export function ensureItemTextures(scene: Phaser.Scene): void {
  if (scene.textures.exists(ITEM_TEXTURES.bone)) return;
  const g = graphics(scene);

  const bone = (key: string, fill: number, edge: number) => {
    g.fillStyle(edge, 1);
    g.fillRoundedRect(4, 5, 20, 6, 3);
    g.fillCircle(5, 5, 4.5);
    g.fillCircle(5, 11, 4.5);
    g.fillCircle(23, 5, 4.5);
    g.fillCircle(23, 11, 4.5);
    g.fillStyle(fill, 1);
    g.fillRoundedRect(5, 6, 18, 4, 2);
    g.fillCircle(5, 5, 3.2);
    g.fillCircle(5, 11, 3.2);
    g.fillCircle(23, 5, 3.2);
    g.fillCircle(23, 11, 3.2);
    g.generateTexture(key, 28, 16);
    g.clear();
  };
  bone(ITEM_TEXTURES.bone, 0xfff8ea, 0xb8a98e);
  bone(ITEM_TEXTURES.golden, 0xffd84a, 0xb8860b);

  // Feather (double jump): light-blue quill.
  g.fillStyle(0x8fd3ff, 1);
  g.fillEllipse(14, 14, 16, 26);
  g.lineStyle(2, 0x3b8ec9, 1);
  g.lineBetween(14, 2, 14, 27);
  g.fillStyle(0xffffff, 0.7);
  g.fillEllipse(11, 11, 6, 12);
  g.generateTexture(ITEM_TEXTURES.feather, 28, 28);
  g.clear();

  // Shield: cyan ring.
  g.lineStyle(4, 0x2ec4b6, 1);
  g.strokeCircle(14, 14, 10);
  g.fillStyle(0x2ec4b6, 0.35);
  g.fillCircle(14, 14, 8);
  g.generateTexture(ITEM_TEXTURES.shield, 28, 28);
  g.clear();

  // Magnet: red horseshoe.
  g.lineStyle(6, 0xe63946, 1);
  g.beginPath();
  g.arc(14, 14, 8, Math.PI, 0, false);
  g.strokePath();
  g.lineBetween(6, 14, 6, 24);
  g.lineBetween(22, 14, 22, 24);
  g.fillStyle(0xf1f1f1, 1);
  g.fillRect(3, 20, 6, 5);
  g.fillRect(19, 20, 6, 5);
  g.generateTexture(ITEM_TEXTURES.magnet, 28, 28);
  g.clear();

  // Boots: orange boot with a wing.
  g.fillStyle(0xf4a261, 1);
  g.fillRoundedRect(6, 4, 10, 18, 3);
  g.fillRoundedRect(6, 16, 18, 8, 3);
  g.fillStyle(0xffffff, 0.9);
  g.fillTriangle(2, 12, 8, 6, 8, 14);
  g.generateTexture(ITEM_TEXTURES.boots, 28, 28);
  g.clear();

  // Spikes tile (32×24): three triangles.
  g.fillStyle(0xd8d8e0, 1);
  for (let i = 0; i < 3; i++) g.fillTriangle(i * 11, 24, i * 11 + 5, 2, i * 11 + 10, 24);
  g.lineStyle(1, 0x6a6a78, 1);
  for (let i = 0; i < 3; i++) g.strokeTriangle(i * 11, 24, i * 11 + 5, 2, i * 11 + 10, 24);
  g.generateTexture(ITEM_TEXTURES.spikes, 32, 24);
  g.clear();

  // Spring pad (56×16).
  g.fillStyle(0x444a55, 1);
  g.fillRoundedRect(0, 8, 56, 8, 3);
  g.fillStyle(0xffb703, 1);
  g.fillRoundedRect(4, 0, 48, 9, 3);
  g.lineStyle(2, 0x8a5a00, 1);
  g.strokeRoundedRect(4, 0, 48, 9, 3);
  g.generateTexture(ITEM_TEXTURES.spring, 56, 16);
  g.clear();

  // Checkpoint flag (32×64).
  g.fillStyle(0x5a4632, 1);
  g.fillRect(4, 0, 4, 64);
  g.fillStyle(0xff5d5d, 1);
  g.fillTriangle(8, 2, 32, 12, 8, 24);
  g.generateTexture(ITEM_TEXTURES.flag, 32, 64);
  g.clear();

  // Heart (22×20).
  g.fillStyle(0xe63946, 1);
  g.fillCircle(6, 6, 6);
  g.fillCircle(16, 6, 6);
  g.fillTriangle(0, 8, 22, 8, 11, 20);
  g.generateTexture(ITEM_TEXTURES.heart, 22, 20);
  g.clear();

  // Rolling thorn: dark ball with eight spikes and a highlight.
  g.fillStyle(0x3b3b46, 1);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const ax = 22 + Math.cos(a) * 21;
    const ay = 22 + Math.sin(a) * 21;
    const bx = 22 + Math.cos(a + 0.28) * 13;
    const by = 22 + Math.sin(a + 0.28) * 13;
    const cx = 22 + Math.cos(a - 0.28) * 13;
    const cy = 22 + Math.sin(a - 0.28) * 13;
    g.fillTriangle(ax, ay, bx, by, cx, cy);
  }
  g.fillStyle(0x55555f, 1);
  g.fillCircle(22, 22, 14);
  g.fillStyle(0x8a8a96, 1);
  g.fillCircle(17, 17, 5);
  g.generateTexture(ITEM_TEXTURES.roller, 44, 44);
  g.clear();

  // Ice cap: pale blue strip with sparkles (tiled on top of ice ground).
  g.fillStyle(0xcfe9ff, 1);
  g.fillRect(0, 0, 64, 24);
  g.fillStyle(0xffffff, 0.9);
  g.fillRect(0, 0, 64, 4);
  g.fillStyle(0x9fd0f5, 1);
  g.fillRect(0, 18, 64, 6);
  g.fillStyle(0xffffff, 1);
  for (const [x, y] of [
    [9, 9],
    [30, 13],
    [50, 8],
  ] as const) {
    g.fillRect(x - 3, y, 7, 1);
    g.fillRect(x, y - 3, 1, 7);
  }
  g.generateTexture(ITEM_TEXTURES.ice, 64, 24);
  g.clear();

  // Beetle: dark oval shell, six legs, two eyes.
  g.fillStyle(0x2f2a3a, 1);
  for (let i = 0; i < 3; i++) {
    g.fillRect(6 + i * 10, 18, 3, 6);
    g.fillRect(24 + i * 3, 18, 3, 6);
  }
  g.fillStyle(0x6b3fa0, 1);
  g.fillEllipse(18, 13, 30, 18);
  g.fillStyle(0x9a6ad0, 1);
  g.fillEllipse(14, 10, 12, 8);
  g.fillStyle(0xffffff, 1);
  g.fillCircle(29, 10, 2.5);
  g.fillStyle(0x000000, 1);
  g.fillCircle(30, 10, 1.2);
  g.generateTexture(ITEM_TEXTURES.beetle, 36, 24);
  g.clear();

  // Bat: body with two spread wings.
  g.fillStyle(0x3a2f4a, 1);
  g.fillTriangle(0, 10, 16, 2, 16, 16);
  g.fillTriangle(40, 10, 24, 2, 24, 16);
  g.fillEllipse(20, 12, 12, 14);
  g.fillStyle(0xffd23f, 1);
  g.fillCircle(17, 10, 1.6);
  g.fillCircle(23, 10, 1.6);
  g.generateTexture(ITEM_TEXTURES.bat, 40, 24);
  g.clear();

  // Rock: grey boulder with facets.
  g.fillStyle(0x6f6f7a, 1);
  g.fillEllipse(20, 20, 38, 32);
  g.fillStyle(0x8a8a96, 1);
  g.fillTriangle(8, 20, 20, 6, 30, 16);
  g.fillStyle(0x55555f, 1);
  g.fillTriangle(12, 30, 28, 26, 22, 34);
  g.generateTexture(ITEM_TEXTURES.rock, 40, 36);
  g.clear();

  // Wind streaks: translucent horizontal strokes (tiled, scrolled with the wind).
  g.lineStyle(2, 0xffffff, 0.35);
  for (const [x, y, w] of [
    [4, 10, 40],
    [60, 26, 52],
    [20, 44, 30],
    [80, 58, 44],
  ] as const)
    g.lineBetween(x, y, x + w, y);
  g.generateTexture(ITEM_TEXTURES.wind, 128, 64);
  g.clear();

  // Dust mote and shield bubble.
  g.fillStyle(0xffffff, 1);
  g.fillCircle(4, 4, 4);
  g.generateTexture(ITEM_TEXTURES.dust, 8, 8);
  g.clear();
  g.lineStyle(3, 0x2ec4b6, 0.9);
  g.strokeCircle(70, 70, 66);
  g.fillStyle(0x2ec4b6, 0.12);
  g.fillCircle(70, 70, 64);
  g.generateTexture(ITEM_TEXTURES.bubble, 140, 140);
  g.destroy();
}
